<template>
  <div :class="containerClass">
    <page-header
      :title="this.$route.params.id ? 'Edit Skill Map' : 'Add Skill Map'"
      :breadcrumb="breadcrumb"
      :container-class="null"
      class="mb-32pt"
    />

    <page-separator title="Skill Map Details" />

    <b-form class="col-md-12 px-0 page-section pt-0" @submit.prevent="onSubmit" novalidate>
      <b-form-group label="Title" label-for="title" label-class="form-label">
        <b-form-input
          id="title"
          placeholder="Enter skill map's title"
          v-model="skillmap.title"
          required
          ref="titleField"
        />
      </b-form-group>
      <b-form-group label="Description" label-for="description" label-class="form-label">
        <b-form-textarea id="description" :placeholder="`Enter description`" :rows="3" v-model="skillmap.description" />
      </b-form-group>
      <b-form-group
        v-if="getLoggedInUser.role === USER_ROLES.SUPER_ADMIN"
        :label="'Program'"
        label-for="program"
        label-class="form-label"
      >
        <v-select
          id="program"
          class="form-control v-select-custom"
          label="title"
          v-model="skillmap.program"
          :reduce="program => program.id"
          placeholder="Select program"
          :options="allPrograms"
          :loading="areProgramsLoading"
          :class="!$v.skillmap.program.required && $v.skillmap.program.$dirty ? 'form-control is-invalid' : ''"
          ref="descpField"
        >
          <template #search="{ attributes, events }">
            <input class="vs__search" :required="!skillmap.program" v-bind="attributes" v-on="events" />
          </template>

          <template slot="option" slot-scope="option">
            <div class="d-flex align-items-center">
              <fmv-avatar :title="true" title-class="bg-transparent" rounded no-link slot="aside" size="xs">
                <b-img :src="option.image_url" class="img-fluid" width="20" alt="Logo" v-if="option.image_url" />
                <i class="fas fa-university fa-lg" v-else></i>
              </fmv-avatar>
              <span>{{ option.title }}</span>
            </div>
          </template>
          <template slot="selected-option" slot-scope="option">
            {{ option.title }}
          </template>
          <b-form-invalid-feedback :state="!$v.skillmap.program.required && $v.skillmap.program.$dirty ? false : null"
            >This field is required.</b-form-invalid-feedback
          >
        </v-select>
      </b-form-group>

      <b-form-group label="State" label-for="state" label-class="form-label" class="">
        <v-select
          id="state"
          class="form-control v-select-custom"
          label="text"
          :state="!$v.skillmap.state.required && $v.skillmap.state.$dirty ? false : null"
          v-model="skillmap.state"
          :reduce="state => state.value"
          placeholder="Select State"
          :options="stateOptions"
          ref="stateField"
        >
          <template #search="{ attributes, events }">
            <input class="vs__search" :required="!skillmap.state" v-bind="attributes" v-on="events" />
          </template>
        </v-select>
        <b-form-invalid-feedback>This field is required.</b-form-invalid-feedback>
      </b-form-group>

      <b-form-group label-class="form-label">
        <b-form-checkbox id="detail" v-model="skillmap.isActive" name="check-button" switch>
          Is Active?
        </b-form-checkbox>
      </b-form-group>
      <b-form-group label-class="form-label">
        <b-form-checkbox id="require-patient" v-model="skillmap.requirePatient" name="check-button" switch>
          Require's Patient?
        </b-form-checkbox>
      </b-form-group>

      <div class="mb-2">
        <page-separator title="Skill Map Steps" />
        <div class="d-flex justify-content-end">
          <b-btn variant="primary" @click.prevent="addSkillMapStep" size="sm" :disabled="isLoading">
            <span>Add Step</span>
          </b-btn>
        </div>

        <span v-if="isStepRequired" style="color: red;">This field is required.</span>
        <i v-else-if="!get(skillmap, 'skillSteps.length')">No skill map steps added.</i>

        <div v-for="(task, index) in skillmap.skillSteps" :key="`task-${index}`">
          <div class="mb-4">
            <div class="d-flex justify-content-between">
              <div class="w-100">
                <b-form-group :label="`Step ${index + 1}`" :label-for="`task-${index}`" label-class="form-label">
                  <b-form-input
                    :id="`task-${index}`"
                    placeholder="Enter step info."
                    v-model="task.step"
                    required
                    :disabled="isLoading"
                    ref="stepInputs"
                    ref-in-for
                  />
                </b-form-group>
              </div>

              <div class="d-flex align-items-center ml-2">
                <b-btn
                  class="mt-3"
                  variant="danger"
                  :disabled="isLoading"
                  @click.prevent="removeSkillMapStep(index)"
                  size="sm"
                >
                  <span>Remove</span>
                </b-btn>
              </div>
            </div>
            <page-separator class="ml-4 p-0 mb-2">
              <div class="w-100">
                <b-link v-b-toggle="`skillstep-${index}-anchors`" class="accordion__toggle p-0">
                  <span class="flex">Anchors</span>
                  <md-icon class="accordion__toggle-icon">
                    {{ anchorVisible[index] ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}
                  </md-icon>
                </b-link>
              </div>
            </page-separator>

            <b-collapse
              :id="`skillstep-${index}-anchors`"
              :accordion="`skillstep-anchors-${index}`"
              :visible="anchorVisible[index]"
              class="accordion__menu"
              @show="$set(anchorVisible, index, true)"
              @hide="$set(anchorVisible, index, false)"
            >
              <b-button style=" display:flex; justify-self: end;" size="sm" variant="primary" @click="addAnchor(task)">
                Add Anchor
              </b-button>
              <div class="ml-5" v-if="task.anchors">
                <div
                  v-for="(anchor, index2) in task.anchors"
                  :key="`anchorfield-${index2}-${index}`"
                  class="d-flex justify-content-between"
                >
                  <div class="w-100">
                    <b-form-group
                      :label="`Anchor ${index2 + 1}`"
                      :label-for="`anchor-${index}-${index2}`"
                      label-class="form-label"
                    >
                      <b-form-input
                        :name="`anchor-${index}-${index2}`"
                        :id="`anchor-${index}-${index2}`"
                        placeholder="Enter anchor info."
                        v-model="task.anchors[index2]"
                        :disabled="isLoading"
                        required
                        ref="anchorInputs"
                        ref-in-for
                      />
                    </b-form-group>
                  </div>
                  <div class="d-flex align-items-center ml-2">
                    <a
                      href="#"
                      class="mt-3"
                      :disabled="true"
                      @click.prevent="removeSkillStepAnchor(task, index2)"
                      v-b-popover.hover.right
                      :title="'Remove'"
                    >
                      <i class="material-icons text-danger">delete</i>
                    </a>
                  </div>
                </div>
              </div>
            </b-collapse>
          </div>
        </div>
      </div>
      <b-btn variant="primary mt-2" :disabled="isFormLoading" style="width: 150px" type="submit" class="btn-normal">
        <i v-if="isLoading" class="fas fa-circle-notch fa-spin"></i>
        <span v-else>{{ $route.params.id ? 'Update' : 'Post' }}</span>
      </b-btn>
    </b-form>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { FmvAvatar } from 'fmv-avatar';
import PageHeader from '@/components/Ui/PageHeader.vue';
import PageSeparator from '@/components/Ui/PageSeparator.vue';
import Page from '../../../components/Page.vue';
import { USER_ROLES } from '@/common/constants';
import { requiredIf } from 'vuelidate/lib/validators';
import { map, get } from 'lodash';
var UsaStates = require('usa-states').UsaStates;
export default {
  components: { PageHeader, PageSeparator, FmvAvatar }, //FmvAvatar
  extends: Page,

  data() {
    return {
      title: this.$route.params.id ? 'Edit Skill Map' : 'Add Skill Map',
      isLoading: false,

      skillmap: {
        program: null,
        title: '',
        description: '',
        state: '',
        isActive: true,
        skillSteps: [],
        requirePatient: true,
      },
      stateOptions: [],
      allPrograms: [],
      areProgramsLoading: false,
      isStepRequired: false,
      anchorVisible: [],
      USER_ROLES,
    };
  },
  validations() {
    return {
      skillmap: {
        program: {
          required: requiredIf(() => this.getLoggedInUser.role === USER_ROLES.SUPER_ADMIN),
        },
        title: {
          required: requiredIf(() => this.getLoggedInUser.role === USER_ROLES.SUPER_ADMIN),
        },
        state: {
          required: requiredIf(() => this.getLoggedInUser.role === USER_ROLES.SUPER_ADMIN),
        },
        skillSteps: {
          required: requiredIf(() => this.getLoggedInUser.role === USER_ROLES.SUPER_ADMIN),
        },
      },
    };
  },
  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),
    breadcrumb() {
      return [
        { text: this.$t('home'), to: this.routes.home },
        {
          text: 'Skill Map',
          to: { name: 'manage-skillmaps' },
        },
        {
          text: this.$route.params.id ? 'Edit Skill Map' : 'Add Skill Map',
          active: true,
        },
      ];
    },
    isFormLoading() {
      return this.isLoading;
    },
  },

  methods: {
    get,
    ...mapActions('program', ['getAllPrograms']),
    ...mapActions('aiSkills', ['getSkillMap', 'updateSkillMap', 'createSkillMap']),

    fillInOptions() {
      var usStates = new UsaStates();
      this.stateOptions = this.stateOptions.concat(
        map(usStates.states, state => ({
          value: state.name,
          text: state.name,
        })),
      );
    },
    addSkillMapStep() {
      this.isStepRequired = false;
      this.skillmap.skillSteps.push({ step: '', anchors: [] });
      this.anchorVisible.push(false);
    },

    removeSkillMapStep(index) {
      this.skillmap.skillSteps.splice(index, 1);
      this.anchorVisible.splice(index, 1);
      this.isStepRequired = this.skillmap.skillSteps.length === 0 || this.skillmap.skillSteps[0].step === '';
    },
    addAnchor(step) {
      this.skillmap.skillSteps.forEach(skillstep => {
        if (skillstep.step === step.step) {
          if (!skillstep.anchors) {
            skillstep.anchors = [''];
          }
          skillstep.anchors.push('');
        }
      });
    },
    removeSkillStepAnchor(step, index) {
      this.skillmap.skillSteps.forEach(skillstep => {
        if (skillstep.step === step.step) {
          skillstep.anchors.splice(index, 1);
        }
      });
    },
    getNativeInput(componentOrElement) {
      return componentOrElement?.$el?.querySelector('input, textarea') || componentOrElement?.$el || componentOrElement;
    },
    async checkFormValidation() {
      const stepInputs = this.$refs.stepInputs || [];
      const anchorInputs = this.$refs.anchorInputs || [];
      const titleField = this.$refs.titleField || [];
      const descpField = this.$refs.descpField || [];
      const stateField = this.$refs.stateField || [];
      const allInputs = [
        ...(Array.isArray(titleField) ? titleField : [titleField]),
        ...(Array.isArray(descpField) ? descpField : [descpField]),
        ...(Array.isArray(stateField) ? stateField : [stateField]),
        ...stepInputs,
        ...anchorInputs,
      ];

      let firstInvalid = null;
      let stepIndexToOpen = null;

      for (const input of allInputs) {
        const rawInput = this.getNativeInput(input);
        if (!rawInput.checkValidity()) {
          firstInvalid = rawInput;

          // If it's an anchor field, open its collapse
          const anchorMatch = rawInput.id.match(/anchor-(\d+)-\d+/);
          if (anchorMatch) {
            stepIndexToOpen = parseInt(anchorMatch[1], 10);
          }

          break;
        }
      }

      if (firstInvalid) {
        if (stepIndexToOpen !== null) {
          this.$set(this.anchorVisible, stepIndexToOpen, true);
          await this.$nextTick();
          setTimeout(() => {
            firstInvalid.focus();
            firstInvalid.reportValidity();
          }, 300);
        } else {
          firstInvalid.focus();
          firstInvalid.reportValidity();
        }
        return;
      }

      return true;
    },
    async onSubmit() {
      const isValid = await this.checkFormValidation();
      this.isLoading = true;
      this.$v.$touch();
      if (!this.$v.$invalid && isValid) {
        try {
          const data = {
            title: this.skillmap.title,
            description: this.skillmap.description,
            program: this.skillmap.program,
            is_active: this.skillmap.isActive,
            requires_patient: this.skillmap.requirePatient,
            state: this.skillmap.state,
            steps: this.skillmap.skillSteps,
          };
          if (this.$route.params.id) {
            await this.updateSkillMap({
              id: this.$route.params.id,
              data,
            });
            this.makeToast({ variant: 'success', msg: 'Skill Map Updated Successfully!' });
          } else {
            await this.createSkillMap(data);
            this.makeToast({ variant: 'success', msg: 'Skill Map Added Successfully!' });
          }
          setTimeout(
            () =>
              this.$router.push({
                name: 'manage-skillmaps',
              }),
            250,
          );
        } catch (_err) {
          this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
        }
      } else {
        this.isStepRequired = this.skillmap.skillSteps.length === 0;
        this.makeToast({ variant: 'danger', msg: 'Please fill all fields correctly.' });
      }
      this.isLoading = false;
    },
    async fetchPrograms() {
      this.areProgramsLoading = true;

      const response = await this.getAllPrograms();
      this.allPrograms = response.data;

      this.areProgramsLoading = false;
    },
  },

  async mounted() {
    this.isLoading = true;

    this.fetchPrograms();
    this.fillInOptions();
    try {
      if (this.$route.params.id) {
        const res = await this.getSkillMap(this.$route.params.id);
        this.skillmap.program = res.data.program;
        this.skillmap.state = res.data.state;
        this.skillmap.title = res.data.title;
        this.skillmap.description = res.data.description;
        this.skillmap.isActive = res.data.is_active;
        this.skillmap.requirePatient = res.data.requires_patient;
        this.skillmap.skillSteps = res.data.steps;
        this.skillmap.skillSteps.forEach(() => this.anchorVisible.push(false));
      }
    } catch (e) {
      this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      this.$router.push({
        name: 'manage-skillmaps',
      });
    }

    this.isLoading = false;
  },
};
</script>
